import type {
  CompetitionTeamBrowseRow,
  MyTeamPayload,
  PendingJoinRequest,
  Team,
  TeamJoinRequest,
} from "../types/database";
import { supabase } from "./supabase";

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

export type CreateTeamInput = {
  competitionId: string;
  name: string;
  visibility: "public" | "private";
};

export async function createTeam(input: CreateTeamInput): Promise<{ team: Team | null; error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { team: null, error: new Error(userError?.message ?? "Not signed in") };
  }

  const visibility = input.visibility === "private" ? "private" : "public";

  const { data, error } = await supabase
    .from("teams")
    .insert({
      competition_id: input.competitionId,
      name: input.name.trim(),
      visibility,
      owner_id: userData.user.id,
    })
    .select("*")
    .single();

  if (error || !data) return { team: null, error: new Error(error?.message ?? "Failed to create team") };
  return { team: data as Team, error: null };
}

export async function listMyTeam(competitionId: string): Promise<MyTeamPayload | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return null;

  const uid = userData.user.id;

  const { data: membershipRows, error: memErr } = await supabase
    .from("team_members")
    .select(
      "team_id, teams ( id, competition_id, name, visibility, owner_id, is_locked, mute_requests, created_at )"
    )
    .eq("student_id", uid);

  if (memErr || !membershipRows?.length) return null;

  let team: Team | null = null;
  for (const row of membershipRows) {
    const t = unwrapOne(row.teams as Team | Team[] | null);
    if (t && t.competition_id === competitionId) {
      team = t;
      break;
    }
  }
  if (!team) return null;

  const { data: members, error: memFetchErr } = await supabase
    .from("team_members")
    .select("team_id, student_id, competition_id, joined_at")
    .eq("team_id", team.id);

  if (memFetchErr || !members) return { team, members: [], is_owner: team.owner_id === uid };

  const studentIds = [...new Set([...members.map((m) => m.student_id), team.owner_id])];
  const { data: profiles } = await supabase.from("user_profiles").select("id, full_name").in("id", studentIds);

  const nameByStudent = new Map<string, string | null>();
  for (const p of profiles ?? []) {
    nameByStudent.set(p.id, p.full_name ?? null);
  }

  let memberRows: { student_id: string; full_name: string | null }[] = members.map((m) => ({
    student_id: m.student_id,
    full_name: nameByStudent.get(m.student_id) ?? null,
  }));

  if (!memberRows.some((r) => r.student_id === team.owner_id)) {
    memberRows = [
      ...memberRows,
      {
        student_id: team.owner_id,
        full_name: nameByStudent.get(team.owner_id) ?? null,
      },
    ];
  }

  memberRows.sort((a, b) => {
    if (a.student_id === team.owner_id) return -1;
    if (b.student_id === team.owner_id) return 1;
    return 0;
  });

  return {
    team,
    members: memberRows,
    is_owner: team.owner_id === uid,
  };
}

export async function listTeamsForCompetition(competitionId: string): Promise<CompetitionTeamBrowseRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id ?? null;

  const { data: comp } = await supabase.from("competitions").select("max_team_size").eq("id", competitionId).maybeSingle();
  const maxTeam = typeof comp?.max_team_size === "number" ? comp.max_team_size : 4;

  const { data: teams, error } = await supabase
    .from("teams")
    .select("id, name, visibility, is_locked, mute_requests, description, looking_for")
    .eq("competition_id", competitionId);

  if (error || !teams?.length) return [];

  // Pull all (team_id, student_id, is_captain) rows for these teams in
  // one shot, then resolve student names + avatars in a second batched
  // query — N+1 across teams would be terrible if there are many.
  const teamIds = teams.map((t) => t.id);
  const { data: memberRows } = await supabase
    .from("team_members")
    .select("team_id, student_id, is_captain")
    .in("team_id", teamIds);

  const studentIds = [
    ...new Set((memberRows ?? []).map((m) => (m as { student_id: string }).student_id)),
  ];
  const profileById = new Map<string, { full_name: string | null; avatar_url: string | null }>();
  if (studentIds.length > 0) {
    const { data: profiles } = await supabase
      .from("user_profiles")
      .select("id, full_name, avatar_url")
      .in("id", studentIds);
    for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null; avatar_url: string | null }>) {
      profileById.set(p.id, { full_name: p.full_name ?? null, avatar_url: p.avatar_url ?? null });
    }
  }

  // Bucket members by team and sort: captain first, then alphabetical.
  const membersByTeam = new Map<string, CompetitionTeamBrowseRow["members"]>();
  for (const m of (memberRows ?? []) as Array<{ team_id: string; student_id: string; is_captain: boolean | null }>) {
    const profile = profileById.get(m.student_id) ?? { full_name: null, avatar_url: null };
    const list = membersByTeam.get(m.team_id) ?? [];
    list.push({
      student_id: m.student_id,
      full_name: profile.full_name,
      avatar_url: profile.avatar_url,
      is_captain: Boolean(m.is_captain),
    });
    membersByTeam.set(m.team_id, list);
  }
  for (const list of membersByTeam.values()) {
    list.sort((a, b) => {
      if (a.is_captain && !b.is_captain) return -1;
      if (!a.is_captain && b.is_captain) return 1;
      return (a.full_name ?? "").localeCompare(b.full_name ?? "");
    });
  }

  const out: CompetitionTeamBrowseRow[] = [];
  for (const t of teams) {
    const members = membersByTeam.get(t.id) ?? [];
    out.push({
      id: t.id,
      name: t.name,
      visibility: t.visibility as CompetitionTeamBrowseRow["visibility"],
      member_count: members.length,
      max_team_size: maxTeam,
      is_locked: Boolean(t.is_locked),
      mute_requests: Boolean(t.mute_requests),
      description: (t as { description: string | null }).description ?? null,
      looking_for: (t as { looking_for: string | null }).looking_for ?? null,
      members,
    });
  }

  if (uid) {
    const excludeIds = new Set<string>();
    const { data: mems } = await supabase.from("team_members").select("team_id").eq("student_id", uid);
    for (const m of mems ?? []) {
      if (m.team_id) excludeIds.add(m.team_id);
    }
    const { data: pending } = await supabase
      .from("team_join_requests")
      .select("team_id")
      .eq("student_id", uid)
      .eq("status", "pending");
    for (const p of pending ?? []) {
      if (p.team_id) excludeIds.add(p.team_id);
    }
    // Hide teams whose captains have ignored this user. The ignored
    // student keeps RLS read access on their own team_ignored_users
    // rows so this query returns directly.
    const { data: ignored } = await supabase
      .from("team_ignored_users")
      .select("team_id")
      .eq("student_id", uid);
    for (const r of ignored ?? []) {
      if ((r as { team_id?: string | null }).team_id) {
        excludeIds.add((r as { team_id: string }).team_id);
      }
    }
    return out.filter((row) => !excludeIds.has(row.id));
  }

  return out;
}

export async function joinPublicTeam(teamId: string): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const { data: team, error: teamErr } = await supabase.from("teams").select("id, visibility, is_locked, competition_id").eq("id", teamId).maybeSingle();

  if (teamErr || !team) return { error: new Error("Team not found") };
  if (team.visibility !== "public") return { error: new Error("This team is not public.") };
  if (team.is_locked) return { error: new Error("This team is locked.") };

  const { data: comp } = await supabase.from("competitions").select("max_team_size").eq("id", team.competition_id).maybeSingle();
  const maxSize = typeof comp?.max_team_size === "number" ? comp.max_team_size : 99;

  const { count } = await supabase
    .from("team_members")
    .select("team_id", { count: "exact", head: true })
    .eq("team_id", teamId);

  if ((count ?? 0) >= maxSize) return { error: new Error("Team is full.") };

  const { error } = await supabase.from("team_members").insert({
    team_id: teamId,
    student_id: userData.user.id,
  });

  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function requestToJoin(teamId: string): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const { data: team, error: teamErr } = await supabase
    .from("teams")
    .select("id, mute_requests, is_locked")
    .eq("id", teamId)
    .maybeSingle();
  if (teamErr || !team) return { error: new Error("Team not found") };
  if (team.is_locked) return { error: new Error("This team is locked.") };
  if (team.mute_requests) return { error: new Error("This team is not accepting join requests.") };

  const { error } = await supabase.from("team_join_requests").insert({
    team_id: teamId,
    student_id: userData.user.id,
    status: "pending",
  });

  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function acceptJoinRequest(requestId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("accept_join_request", { request_id: requestId });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function cancelJoinRequest(requestId: string): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const { error } = await supabase
    .from("team_join_requests")
    .update({ status: "cancelled" })
    .eq("id", requestId)
    .eq("student_id", userData.user.id);

  if (error) return { error: new Error(error.message) };
  return { error: null };
}

/**
 * Captain-side decline of a pending join request. Marks the request as
 * declined; does NOT block future requests from this requester. Use
 * ignoreTeamRequester() if the captain wants to block re-requests too.
 */
export async function declineJoinRequest(
  requestId: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("decline_join_request", {
    p_request_id: requestId,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

/**
 * Captain-side decline + ignore. Marks the pending request declined AND
 * inserts the requester into the team's ignore list so any future
 * `team_join_requests` insert from them fires the block trigger and is
 * rejected with "You can no longer request to join this team."
 */
export async function ignoreTeamRequester(
  requestId: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("ignore_team_requester", {
    p_request_id: requestId,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

/**
 * Lift a team-level ignore. Caller must be a captain of the team.
 */
export async function unignoreTeamRequester(
  teamId: string,
  studentId: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("unignore_team_requester", {
    p_team_id: teamId,
    p_student_id: studentId,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export type IgnoredRequesterRow = {
  team_id: string;
  team_name: string;
  competition_id: string;
  competition_name: string;
  student_id: string;
  student_name: string | null;
  student_avatar: string | null;
  ignored_at: string;
};

/**
 * For the current user (acting as captain anywhere): the full list of
 * (team, requester) pairs they've ignored. Drives the Settings overlay's
 * "Ignored requesters" section so they can unignore.
 */
export async function listMyIgnoredRequesters(): Promise<IgnoredRequesterRow[]> {
  const { data, error } = await supabase.rpc("list_my_ignored_requesters");
  if (error || !Array.isArray(data)) return [];
  return data as IgnoredRequesterRow[];
}

export async function listPendingJoinRequests(teamId: string): Promise<PendingJoinRequest[]> {
  const { data, error } = await supabase
    .from("team_join_requests")
    .select("*")
    .eq("team_id", teamId)
    .eq("status", "pending");

  if (error || !data) return [];

  const studentIds = [...new Set(data.map((r) => (r as TeamJoinRequest).student_id).filter(Boolean))];
  if (studentIds.length === 0) return [];

  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name, avatar_url")
    .in("id", studentIds);

  const profileById = new Map<string, { full_name: string | null; avatar_url: string | null }>();
  for (const p of profiles ?? []) {
    profileById.set(p.id, {
      full_name: (p as { full_name: string | null }).full_name ?? null,
      avatar_url: (p as { avatar_url: string | null }).avatar_url ?? null,
    });
  }

  return data.map((row) => {
    const r = row as TeamJoinRequest;
    const p = profileById.get(r.student_id);
    return {
      ...r,
      full_name: p?.full_name ?? null,
      avatar_url: p?.avatar_url ?? null,
    };
  });
}

export async function kickMember(teamId: string, studentId: string): Promise<{ error: Error | null }> {
  // Captain "kick" goes through a SECURITY DEFINER RPC that stamps
  // removed_by_user_id and DELETEs in a single transaction. The previous
  // approach (update + delete via PostgREST) silently lost the stamp
  // because team_members has no RLS UPDATE policy, so the AFTER DELETE
  // trigger emitted "X left the team" instead of "X was removed by Y".
  const { error } = await supabase.rpc("kick_team_member", {
    p_team_id: teamId,
    p_student_id: studentId,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function leaveTeam(teamId: string): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const { error } = await supabase.from("team_members").delete().eq("team_id", teamId).eq("student_id", userData.user.id);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function setTeamVisibility(
  teamId: string,
  visibility: "public" | "private"
): Promise<{ error: Error | null }> {
  const v = visibility === "private" ? "private" : "public";
  const { error } = await supabase.from("teams").update({ visibility: v }).eq("id", teamId);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function lockTeam(teamId: string, locked: boolean): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("teams").update({ is_locked: locked }).eq("id", teamId);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function setMuteRequests(teamId: string, muted: boolean): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("teams").update({ mute_requests: muted }).eq("id", teamId);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

/**
 * Captain updates the team's pitch fields. Called from the team page editor;
 * inputs are validated to schema length caps (280 / 140) before reaching the
 * DB check constraints, so users get a friendlier error message.
 */
export async function updateTeamPitch(
  teamId: string,
  patch: { description?: string | null; looking_for?: string | null }
): Promise<{ error: Error | null }> {
  const desc = patch.description == null ? null : patch.description.trim() || null;
  const lf = patch.looking_for == null ? null : patch.looking_for.trim() || null;
  if (desc != null && desc.length > 280) return { error: new Error("Description is too long (max 280 chars).") };
  if (lf != null && lf.length > 140) return { error: new Error("\"Looking for\" is too long (max 140 chars).") };
  const updates: Record<string, string | null> = {};
  if (patch.description !== undefined) updates.description = desc;
  if (patch.looking_for !== undefined) updates.looking_for = lf;
  if (Object.keys(updates).length === 0) return { error: null };
  const { error } = await supabase.from("teams").update(updates).eq("id", teamId);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function deleteTeam(teamId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("teams").delete().eq("id", teamId);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}
